import { createStore } from './util'
import { useApp } from './app'
import { notify, errorLog } from '@/utils'
import {
  User,
  OperatorPointsResponse,
  OperatorReferralStatsResponse,
  OperatorReferralTreeResponse,
  FlaggedUser,
  OperatorSocialReward,
  OperatorLogItem,
  GiveBonusPointsRequest,
  GiveBonusPointsResponse,
  OperatorEarnedPointsResponse,
  OperatorLegacyPointsResponse,
} from '@/types'

interface OperatorStore {
  loading: boolean
  searchEmail: string
  selectedUser: User | null
  points: OperatorPointsResponse | null
  earnedPoints: OperatorEarnedPointsResponse | null
  legacyPoints: OperatorLegacyPointsResponse | null
  referralStats: OperatorReferralStatsResponse | null
  referralTree: OperatorReferralTreeResponse | null
  flags: FlaggedUser[]
  socialRewards: OperatorSocialReward[]
  auditLogs: OperatorLogItem[]
  sectionLoading: Record<string, boolean>

  setSearchEmail: (email: string) => void
  searchUserByEmail: (email: string) => Promise<boolean>
  fetchUserPoints: (userId: string) => Promise<void>
  fetchEarnedPoints: (userId: string) => Promise<void>
  fetchLegacyPoints: (userId: string) => Promise<void>
  fetchReferralStats: (userId: string) => Promise<void>
  fetchReferralTree: (userId: string) => Promise<void>
  fetchFlags: (userId: string) => Promise<void>
  fetchSocialRewards: (userId: string) => Promise<void>
  fetchAuditLogs: (userId: string) => Promise<void>
  giveBonusPoints: (userId: string, points: number, reason: string) => Promise<boolean>
  unsuspendUser: (userId: string) => Promise<boolean>
  deleteFlag: (flagId: string) => Promise<boolean>
  resetState: () => void
  setSectionLoading: (section: string, loading: boolean) => void
}

export const useOperator = createStore<OperatorStore>(
  'operator',
  (set, get) => ({
    loading: false,
    searchEmail: '',
    selectedUser: null,
    points: null,
    earnedPoints: null,
    legacyPoints: null,
    referralStats: null,
    referralTree: null,
    flags: [],
    socialRewards: [],
    auditLogs: [],
    sectionLoading: {},

    setSearchEmail: (email) => set({ searchEmail: email }),

    setSectionLoading: (section, loading) => {
      const current = get().sectionLoading
      set({ sectionLoading: { ...current, [section]: loading } })
    },

    searchUserByEmail: async (email: string): Promise<boolean> => {
      const http = useApp.getState().http
      set({ loading: true })

      try {
        // Convert email to lowercase for consistent searching
        const normalizedEmail = email.toLowerCase()
        const response = await http.get<User>(
          `/operator/user?email=${encodeURIComponent(normalizedEmail)}`,
        )

        if (!response.data) {
          notify.error('User not found')
          return false
        }

        set({
          selectedUser: response.data,
          searchEmail: email,
        })

        notify.success('User found')
        return true
      } catch (error) {
        errorLog(error)
        notify.error('Failed to find user')
        set({ selectedUser: null })
        return false
      } finally {
        set({ loading: false })
      }
    },

    fetchUserPoints: async (userId: string): Promise<void> => {
      const http = useApp.getState().http
      get().setSectionLoading('points', true)

      try {
        const response = await http.get<OperatorPointsResponse>(`/operator/user/${userId}/points`)

        if (!response.data) {
          throw new Error('No data returned')
        }

        set({ points: response.data })
      } catch (error) {
        errorLog(error)
        notify.error('Failed to fetch points')
        set({ points: null })
      } finally {
        get().setSectionLoading('points', false)
      }
    },

    fetchEarnedPoints: async (userId: string): Promise<void> => {
      const http = useApp.getState().http
      get().setSectionLoading('earnedPoints', true)

      try {
        const response = await http.get<OperatorEarnedPointsResponse>(
          `/operator/user/${userId}/earned-points`,
        )

        if (!response.data) {
          throw new Error('No data returned')
        }

        set({ earnedPoints: response.data })
      } catch (error) {
        errorLog(error)
        notify.error('Failed to fetch earned points')
        set({ earnedPoints: null })
      } finally {
        get().setSectionLoading('earnedPoints', false)
      }
    },

    fetchLegacyPoints: async (userId: string): Promise<void> => {
      const http = useApp.getState().http
      get().setSectionLoading('legacyPoints', true)

      try {
        const response = await http.get<OperatorLegacyPointsResponse>(
          `/operator/user/${userId}/legacy-points`,
        )

        if (!response.data) {
          throw new Error('No data returned')
        }

        set({ legacyPoints: response.data })
      } catch (error) {
        errorLog(error)
        notify.error('Failed to fetch legacy points')
        set({ legacyPoints: null })
      } finally {
        get().setSectionLoading('legacyPoints', false)
      }
    },

    fetchReferralStats: async (userId: string): Promise<void> => {
      const http = useApp.getState().http
      get().setSectionLoading('referralStats', true)

      try {
        const response = await http.get<OperatorReferralStatsResponse>(
          `/operator/user/${userId}/referrals`,
        )

        if (!response.data) {
          throw new Error('No data returned')
        }

        set({ referralStats: response.data })
      } catch (error) {
        errorLog(error)
        notify.error('Failed to fetch referral stats')
        set({ referralStats: null })
      } finally {
        get().setSectionLoading('referralStats', false)
      }
    },

    fetchReferralTree: async (userId: string): Promise<void> => {
      const http = useApp.getState().http
      get().setSectionLoading('referralTree', true)

      try {
        const response = await http.get<OperatorReferralTreeResponse>(
          `/operator/user/${userId}/referral-tree`,
        )

        if (!response.data) {
          throw new Error('No data returned')
        }

        set({ referralTree: response.data })
      } catch (error) {
        errorLog(error)
        notify.error('Failed to fetch referral tree')
        set({ referralTree: null })
      } finally {
        get().setSectionLoading('referralTree', false)
      }
    },

    fetchFlags: async (userId: string): Promise<void> => {
      const http = useApp.getState().http
      get().setSectionLoading('flags', true)

      try {
        const response = await http.get<{ flags: FlaggedUser[] }>(
          `/operator/user/${userId}/flags`,
        )

        if (!response.data) {
          throw new Error('No data returned')
        }

        set({ flags: response.data.flags || [] })
      } catch (error) {
        errorLog(error)
        notify.error('Failed to fetch flags')
        set({ flags: [] })
      } finally {
        get().setSectionLoading('flags', false)
      }
    },

    fetchSocialRewards: async (userId: string): Promise<void> => {
      const http = useApp.getState().http
      get().setSectionLoading('socialRewards', true)

      try {
        const response = await http.get<{ rewards: OperatorSocialReward[] }>(
          `/operator/user/${userId}/social-rewards`,
        )

        if (!response.data) {
          throw new Error('No data returned')
        }

        set({ socialRewards: response.data.rewards || [] })
      } catch (error) {
        errorLog(error)
        notify.error('Failed to fetch social rewards')
        set({ socialRewards: [] })
      } finally {
        get().setSectionLoading('socialRewards', false)
      }
    },

    fetchAuditLogs: async (userId: string): Promise<void> => {
      const http = useApp.getState().http
      get().setSectionLoading('auditLogs', true)

      try {
        const response = await http.get<{ logs: OperatorLogItem[] }>(
          `/operator/user/${userId}/audit-log`,
        )

        if (!response.data) {
          throw new Error('No data returned')
        }

        set({ auditLogs: response.data.logs || [] })
      } catch (error) {
        errorLog(error)
        notify.error('Failed to fetch audit logs')
        set({ auditLogs: [] })
      } finally {
        get().setSectionLoading('auditLogs', false)
      }
    },

    giveBonusPoints: async (userId: string, points: number, reason: string): Promise<boolean> => {
      const http = useApp.getState().http
      get().setSectionLoading('bonusPoints', true)

      try {
        const request: GiveBonusPointsRequest = {
          userId,
          points,
          reason,
        }

        const response = await http.post<GiveBonusPointsResponse>(
          '/operator/user/bonus-points',
          request,
        )

        if (!response.data || !response.data.success) {
          throw new Error('Failed to give bonus points')
        }

        notify.success('Bonus points given successfully')
        // Refresh audit log and points
        get().fetchAuditLogs(userId)
        get().fetchUserPoints(userId)
        return true
      } catch (error) {
        errorLog(error)
        notify.error('Failed to give bonus points')
        return false
      } finally {
        get().setSectionLoading('bonusPoints', false)
      }
    },

    unsuspendUser: async (userId: string): Promise<boolean> => {
      const http = useApp.getState().http
      try {
        await http.post(`/operator/user/${userId}/unsuspend`)
        const selectedUser = get().selectedUser
        if (selectedUser && selectedUser.id === userId) {
          set({ selectedUser: { ...selectedUser, isSuspended: false } })
        }
        notify.success('User unsuspended successfully')
        // Refresh audit log
        get().fetchAuditLogs(userId)
        return true
      } catch (error) {
        errorLog(error)
        notify.error('Failed to unsuspend user')
        return false
      }
    },

    deleteFlag: async (flagId: string): Promise<boolean> => {
      const http = useApp.getState().http
      try {
        await http.delete(`/operator/flag/${flagId}`)
        const flags = get().flags.filter((f) => f.id !== flagId)
        set({ flags })
        notify.success('Flag removed successfully')
        // Refresh audit log
        const selectedUser = get().selectedUser
        if (selectedUser) {
          get().fetchAuditLogs(selectedUser.id)
        }
        return true
      } catch (error) {
        errorLog(error)
        notify.error('Failed to remove flag')
        return false
      }
    },

    resetState: () => {
      set({
        selectedUser: null,
        points: null,
        earnedPoints: null,
        legacyPoints: null,
        referralStats: null,
        referralTree: null,
        flags: [],
        socialRewards: [],
        auditLogs: [],
        sectionLoading: {},
      })
    },
  }),
  true,
)

